import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, X, CreditCard, Calendar } from 'lucide-react';

const RenewalReminder = () => {
  const { api, user } = useAuth();
  const [reminder, setReminder] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  const fetchReminder = useCallback(async () => {
    // Only show for accounting/admin roles
    if (!user || !['accounting', 'admin'].includes(user.role)) {
      return;
    }

    try {
      const response = await api.get('/payments/renewal-reminder');
      if (response.data?.has_reminder) {
        setReminder(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch renewal reminder:', error);
    }
  }, [api, user]);

  useEffect(() => {
    fetchReminder();
  }, [fetchReminder]);

  const handleDismiss = async () => {
    try {
      await api.post('/payments/dismiss-reminder');
      setDismissed(true);
    } catch (error) {
      console.error('Failed to dismiss reminder:', error);
    }
  };

  if (!reminder || dismissed) {
    return null;
  }

  return (
    <Card className="bg-gradient-to-r from-orange-900/40 to-yellow-900/40 border-orange-600/50 shadow-lg">
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-orange-600/30 rounded-lg">
              <AlertTriangle className="h-5 w-5 text-orange-400" />
            </div>
            <div>
              <h4 className="text-white font-semibold flex items-center gap-2">
                Subscription Renewal Coming Up
                <Badge className="bg-orange-600">{reminder.days_until_renewal} days</Badge>
              </h4>
              <p className="text-slate-300 text-sm mt-1">
                {reminder.message}
              </p>
              <div className="flex items-center gap-4 mt-2 text-sm">
                <span className="text-slate-400 flex items-center gap-1">
                  <Calendar className="h-4 w-4" />
                  {new Date(reminder.renewal_date).toLocaleDateString()}
                </span>
                {reminder.amount && (
                  <span className="text-slate-400 flex items-center gap-1">
                    <CreditCard className="h-4 w-4" />
                    ${reminder.amount}/mo
                  </span>
                )}
              </div>
            </div>
          </div>
          <Button 
            variant="ghost" 
            size="sm"
            onClick={handleDismiss}
            className="text-slate-400 hover:text-white"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default RenewalReminder;
